<template>
  <div class="selection-page mx-4 mt-3">
    <v-btn flat class="pl-0" :to="{ name: 'SelectionIndex' }">
      <v-icon left>arrow_back</v-icon>
      Selections
    </v-btn>

    <v-alert v-if="error" :value="true" color="error" outline>
      {{ error }}
    </v-alert>

    <v-progress-linear v-if="loading" indeterminate color="black"></v-progress-linear>

    <v-card v-if="selection" class="selection-card elevation-0 mt-2">
      <v-card-title class="selection-header px-0 pt-0 pb-4">
        <div>
          <div class="section-label">Selection</div>
          <div class="display-1 mt-1">{{ selection.name }}</div>
          <div class="selection-meta mt-2">
            <span>{{ artworks.length }} artworks</span>
            <span>Created {{ formatDateTime(selection.created_at) }}</span>
            <span v-if="selection.created_by">By {{ get_user(selection.created_by) }}</span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          color="black"
          dark
          :to="{ name: 'NetxSelectionSync', params: { id: selection.id } }"
        >
          <v-icon left>{{ canManageNetx ? 'sync' : 'history' }}</v-icon>
          {{ canManageNetx ? 'NetX sync' : 'NetX runs' }}
        </v-btn>
      </v-card-title>

      <v-card-text class="px-0 pb-0">
        <div class="section-heading">
          <div>
            <div class="title">Artworks</div>
            <div class="caption grey--text">Saved in this selection</div>
          </div>
        </div>

        <v-data-table
          class="selection-table elevation-0"
          :headers="artworkHeaders"
          :items="artworks"
          :pagination.sync="artworkPagination"
          rows-per-page-text="Artworks per page"
          item-key="id"
        >
          <template slot="items" slot-scope="props">
            <tr>
              <td>
                <router-link
                  class="artwork-link"
                  :to="{ name: 'EditArtwork', params: { id: props.item.id, artwork: props.item } }"
                >
                  {{ props.item.title || props.item.name || `Artwork #${props.item.id}` }}
                </router-link>
              </td>
              <td>{{ props.item.accession_number || '—' }}</td>
            </tr>
          </template>
          <template slot="no-data">
            <div class="grey--text py-3">This selection has no artworks.</div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-card v-if="selection" class="selection-card elevation-0 mt-4">
      <v-card-title class="section-heading px-0 pt-0">
        <div>
          <div class="title">NetX sync runs</div>
          <div class="caption grey--text">History and processing results</div>
        </div>
        <v-spacer></v-spacer>
        <v-btn flat small :loading="loadingRuns" @click="loadRuns">
          <v-icon left small>refresh</v-icon>
          Refresh
        </v-btn>
      </v-card-title>
      <v-card-text class="px-0 pb-0">
        <v-data-table
          v-if="runs.length"
          class="selection-table elevation-0"
          :headers="runHeaders"
          :items="runs"
          :pagination.sync="runPagination"
          rows-per-page-text="Runs per page"
          item-key="id"
        >
          <template slot="items" slot-scope="props">
            <tr>
              <td class="font-weight-medium">#{{ props.item.id }}</td>
              <td>{{ formatDateTime(props.item.created_at) }}</td>
              <td>
                <span class="status-pill" :class="statusClass(props.item)">
                  {{ runStatusLabel(props.item) }}
                </span>
              </td>
              <td>
                <strong>{{ props.item.progress.completed }}</strong>
                <span class="grey--text"> / {{ props.item.progress.total }}</span>
                <span v-if="issueCount(props.item)" class="run-issues ml-2">
                  {{ issueCount(props.item) }} issue{{ issueCount(props.item) === 1 ? '' : 's' }}
                </span>
              </td>
              <td class="text-xs-right">
                <v-btn
                  small
                  flat
                  class="mr-0"
                  :to="{
                    name: 'NetxSelectionSync',
                    params: { id: selection.id },
                    query: { run_id: props.item.id }
                  }"
                >
                  Review
                  <v-icon right small>arrow_forward</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
        <div v-else-if="!loadingRuns" class="empty-state grey--text">
          No NetX sync runs have been started for this selection.
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import yaml from 'js-yaml'
import { canSyncNetx } from '@/utils/netxArtworkReview'
import {
  loadSelectionDetails,
  loadLatestNetxListSyncRun,
  listSyncError
} from '@/utils/netxListSync'

export default {
  data () {
    return {
      selection: null,
      runs: [],
      loading: false,
      loadingRuns: false,
      error: '',
      artworkPagination: {
        rowsPerPage: 25,
        sortBy: 'title'
      },
      runPagination: {
        descending: true,
        rowsPerPage: 10,
        sortBy: 'id'
      },
      artworkHeaders: [
        { text: 'Artwork', value: 'title' },
        { text: 'Accession number', value: 'accession_number' }
      ],
      runHeaders: [
        { text: 'Run', value: 'id' },
        { text: 'Started', value: 'created_at' },
        { text: 'Status', value: 'status' },
        { text: 'Progress', value: 'progress' },
        { text: '', value: 'actions', sortable: false, align: 'right' }
      ]
    }
  },
  computed: {
    artworks () {
      if (!this.selection) return []
      const ids = Array.isArray(this.selection.artworks) ? this.selection.artworks : []
      return ids.map(id => this.$store.state.artworks.find(artwork => String(artwork.id) === String(id)) || { id })
    },
    canManageNetx () {
      return canSyncNetx(this.c_user)
    }
  },
  created () {
    this.loadSelection()
    this.loadRuns()
    this.$store.dispatch('loadArtworks')
  },
  methods: {
    async loadSelection () {
      this.loading = true
      this.error = ''
      try {
        const response = await loadSelectionDetails(this.$route.params.id)
        const selection = response.data
        selection.artworks = this.parseArtworkIds(selection.artworks)
        this.selection = selection
      } catch (error) {
        this.error = listSyncError(error)
      } finally {
        this.loading = false
      }
    },
    async loadRuns () {
      this.loadingRuns = true
      this.error = ''
      try {
        const response = await loadLatestNetxListSyncRun(this.$route.params.id)
        this.runs = response.data
      } catch (error) {
        this.error = listSyncError(error)
      } finally {
        this.loadingRuns = false
      }
    },
    parseArtworkIds (value) {
      if (Array.isArray(value)) return value.map(item => typeof item === 'object' ? item.id : item)
      try {
        const parsed = yaml.load(value)
        if (Array.isArray(parsed)) return parsed.map(item => typeof item === 'object' ? item.id : item)
      } catch (error) {
        return []
      }
      return []
    },
    formatDateTime (value) {
      return value ? this.format_date(value, 'YYYY-MM-DD HH:mm') : '—'
    },
    issueCount (run) {
      const counts = run.progress.counts
      return (counts.failed || 0) + (counts.partial || 0) + (counts.no_match || 0)
    },
    runStatusLabel (run) {
      if (run.status === 'completed' && this.issueCount(run)) return 'Completed with issues'
      return run.status.charAt(0).toUpperCase() + run.status.slice(1)
    },
    statusClass (run) {
      if (run.status === 'failed') return 'status-error'
      if (run.status === 'completed' && this.issueCount(run)) return 'status-warning'
      if (run.status === 'completed') return 'status-success'
      if (run.status === 'running') return 'status-active'
      return 'status-neutral'
    }
  }
}
</script>

<style scoped>
.selection-page {
  max-width: 1440px;
}

.selection-card {
  padding: 24px 20px 8px;
}

.selection-header {
  border-bottom: 1px solid #d5d5d5;
}

.section-label {
  color: #777;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.selection-meta {
  color: #666;
  display: flex;
  flex-wrap: wrap;
  font-size: 13px;
  gap: 8px 20px;
}

.section-heading {
  align-items: center;
  border-bottom: 1px solid #d5d5d5;
  display: flex;
  min-height: 58px;
}

.selection-table {
  border-top: 0;
}

.artwork-link {
  color: #111;
  font-weight: 500;
  text-decoration: none;
}

.artwork-link:hover {
  text-decoration: underline;
}

.status-pill {
  border: 1px solid #bbb;
  border-radius: 2px;
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .03em;
  line-height: 22px;
  padding: 0 8px;
  text-transform: uppercase;
}

.status-success {
  border-color: #4f7a58;
  color: #315a3a;
}

.status-warning {
  border-color: #b28a42;
  color: #80601e;
}

.status-error {
  border-color: #b34b4b;
  color: #9a3030;
}

.status-active {
  background: #111;
  border-color: #111;
  color: #fff;
}

.status-neutral {
  color: #666;
}

.run-issues {
  color: #8a641d;
  font-size: 12px;
}

.empty-state {
  border-bottom: 1px solid #e2e2e2;
  padding: 24px 0;
}

@media (max-width: 720px) {
  .selection-card {
    padding-left: 8px;
    padding-right: 8px;
  }

  .selection-header {
    align-items: flex-start;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/selections/Detail.vue